/**
 * IdleLogout — signs the user out after their configured minutes of inactivity.
 *
 * The window is per-user (`auth.user.idle_timeout_seconds`, served on /auth/me)
 * and defaults to null = never — a NOC dashboard left on a wall display stays
 * signed in. Mounted once in the app shell so it covers every authenticated
 * route; it renders nothing.
 *
 * Enforcement is entirely client-side. On timeout we run the app's normal
 * `auth.logout()` (which clears the local Zitadel token — naas does NOT hit
 * Zitadel's end_session endpoint) and hard-redirect to /login?reason=idle. Since
 * every sign-in already forces `prompt: "login"`, the next login still requires
 * credentials even though the Zitadel SSO cookie lingers — so a local logout is
 * a genuine re-auth here, no end_session round-trip needed.
 *
 * Note: this is unrelated to token expiry. naas does no silent renew and uses no
 * refresh token, so the access token's own TTL is a separate clock; this timer
 * only measures user inactivity.
 */
import { useEffect, useRef } from "react";

import { useAuth } from "@/auth/useAuth";

// Poll cadence: we compare `now - lastActivity` against the window every 15s
// rather than arming one long setTimeout, so a backgrounded/throttled tab still
// evaluates the timeout the moment it regains focus.
const POLL_MS = 15_000;

// Activity signals. All passive — we only read a timestamp, never preventDefault.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

export function IdleLogout() {
  const auth = useAuth();
  const seconds =
    auth.status === "authenticated" ? auth.user?.idle_timeout_seconds ?? null : null;

  // Keep the latest logout in a ref so the interval closure never goes stale
  // without re-arming the whole timer on every render.
  const logoutRef = useRef(auth.logout);
  logoutRef.current = auth.logout;

  useEffect(() => {
    // null / 0 → never auto-logout. Nothing to arm.
    if (!seconds || seconds <= 0) return;

    let lastActivity = Date.now();
    const bump = () => {
      lastActivity = Date.now();
    };
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, bump, { passive: true });
    }

    let firing = false;
    const interval = window.setInterval(() => {
      if (firing) return;
      if (Date.now() - lastActivity < seconds * 1000) return;
      firing = true; // guard against a second tick while logout is in flight
      window.clearInterval(interval);
      void logoutRef.current().finally(() => {
        // Hard redirect (not SPA navigate): guarantees the reason banner shows and
        // that all in-memory state is dropped along with the cleared token.
        window.location.replace("/login?reason=idle");
      });
    }, POLL_MS);

    return () => {
      window.clearInterval(interval);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, bump);
      }
    };
  }, [seconds]);

  return null;
}
